import { useState, useCallback, useEffect } from 'react';
import { API_CONFIG, getAuthHeaders } from '../constants/api';
import { triggerAuthUpdate } from '../utils/authUtils';

interface AuthState {
    client_id: string | null;
    access_token: string | null;
    session_id: string | null;
    name: string | null;
    picture: string | null;
    isAuthenticated: boolean;
}

export const useAuth = () => {
    const [authState, setAuthState] = useState<AuthState>({
        client_id: localStorage.getItem("client_id"),
        access_token: localStorage.getItem("access_token"),
        session_id: localStorage.getItem("session_id"),
        name: localStorage.getItem("name"),
        picture: localStorage.getItem("picture"),
        isAuthenticated: !!(localStorage.getItem("client_id") && localStorage.getItem("access_token") && localStorage.getItem("session_id"))
    });

    useEffect(() => {
        const currentClientId = localStorage.getItem("client_id");
        const currentSessionId = localStorage.getItem("session_id");

        if (currentClientId && currentSessionId) {
            const accessToken = localStorage.getItem("access_token");
            const name = localStorage.getItem("name");
            const picture = localStorage.getItem("picture");
            const isAuth = !!(currentClientId && accessToken && currentSessionId);
            setAuthState(prev => ({
                ...prev,
                client_id: currentClientId,
                access_token: accessToken,
                session_id: currentSessionId,
                name,
                picture,
                isAuthenticated: isAuth
            }));
        }
    }, []);

    useEffect(() => {
        const handleStorageChange = () => {
            const currentClientId = localStorage.getItem("client_id");
            const currentSessionId = localStorage.getItem("session_id");
            const currentAccessToken = localStorage.getItem("access_token");
            const currentName = localStorage.getItem("name");
            const currentPicture = localStorage.getItem("picture");

            if (currentClientId && currentSessionId && currentAccessToken) {
                setAuthState(prev => ({
                    ...prev,
                    client_id: currentClientId,
                    access_token: currentAccessToken,
                    session_id: currentSessionId,
                    name: currentName,
                    picture: currentPicture,
                    isAuthenticated: true
                }));
            }
        };

        const handleAuthUpdate = () => {
            handleStorageChange();
        };

        window.addEventListener('storage', handleStorageChange);

        window.addEventListener('focus', handleStorageChange);

        window.addEventListener('authStateUpdate', handleAuthUpdate);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('focus', handleStorageChange);
            window.removeEventListener('authStateUpdate', handleAuthUpdate);
        };
    }, []);

    const requestAccessToken = useCallback(async (auth0Token: string) => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REQUEST_ACCESS_TOKEN}`, {
                method: 'GET',
                headers: {
                    'Authorization': auth0Token,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to request access token');
            }

            const { client_id, access_token, name, picture } = await response.json();



            // Store in localStorage
            localStorage.setItem("client_id", client_id);
            localStorage.setItem("access_token", access_token);
            localStorage.setItem("name", name);
            localStorage.setItem("picture", picture);

            setAuthState(prev => ({
                ...prev,
                client_id,
                access_token,
                name,
                picture
            }));

            return { client_id, access_token, name, picture };
        } catch (error) {
            throw error;
        }
    }, []);

    const createSession = useCallback(async (client_id: string, access_token: string) => {
        try {
            localStorage.removeItem("session_id");
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.NEW_SESSION}`, {
                method: 'GET',
                headers: {
                    'Authorization': `${client_id}:${access_token}`,
                    'Content-Type': 'application/json'
                },
                // credentials: 'include'  // I don't think creadentials are needed here
            });

            if (!response.ok) {
                throw new Error('Failed to create session');
            }

            const { session_id } = await response.json();

            // Store in localStorage
            localStorage.setItem("session_id", session_id);

            setAuthState(prev => ({
                ...prev,
                session_id,
                isAuthenticated: true
            }));

            return session_id;
        } catch (error) {
            throw error;
        }
    }, []);

    const authenticate = useCallback(async (auth0Token: string) => {
        try {
            const { client_id, access_token } = await requestAccessToken(auth0Token);
            const session_id = await createSession(client_id, access_token);

            setAuthState(prev => ({
                ...prev,
                client_id,
                access_token,
                session_id,
                isAuthenticated: true
            }));

            triggerAuthUpdate();

            return true;
        } catch (error) {
            return false;
        }
    }, [requestAccessToken, createSession]);

    const logout = useCallback(() => {
        localStorage.clear();
        sessionStorage.clear();

        setAuthState({
            client_id: null,
            access_token: null,
            session_id: null,
            name: null,
            picture: null,
            isAuthenticated: false
        });
    }, []);

    const getWebSocketUrl = useCallback(() => {
        if (!authState.client_id || !authState.session_id) {
            throw new Error('Not authenticated');
        }

        const wsProtocol = API_CONFIG.BASE_URL.startsWith('https') ? 'wss' : 'ws';
        const baseUrl = API_CONFIG.BASE_URL.replace('https://', '').replace('http://', '');
        const path = `${API_CONFIG.ENDPOINTS.EVENTS_WS}/${authState.client_id}/${authState.session_id}`;
        const wsUrl = `${wsProtocol}://${baseUrl}${path}`;

        return wsUrl;
    }, [authState.client_id, authState.session_id]);

    const refreshAuthState = useCallback(() => {
        const currentClientId = localStorage.getItem("client_id");
        const currentSessionId = localStorage.getItem("session_id");
        const currentAccessToken = localStorage.getItem("access_token");
        const currentName = localStorage.getItem("name");
        const currentPicture = localStorage.getItem("picture");

        if (currentClientId && currentSessionId && currentAccessToken) {
            setAuthState(prev => ({
                ...prev,
                client_id: currentClientId,
                access_token: currentAccessToken,
                session_id: currentSessionId,
                name: currentName,
                picture: currentPicture,
                isAuthenticated: true
            }));
        }
    }, []);

    return {
        ...authState,
        authenticate,
        logout,
        getWebSocketUrl,
        requestAccessToken,
        createSession,
        refreshAuthState,
        isAuthenticated: authState.isAuthenticated
    };
};